import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { TableService } from '../../../../services/table.service';
import { RestaurantService } from '../../../../services/restaurant.service';
import { FNFResponse } from '../../../../models/response';

@Component({
  selector: 'app-add-table',
  standalone: true,
  imports: [ReactiveFormsModule, MatIcon, CommonModule, FormsModule],
  templateUrl: './add-table.component.html',
  styleUrls: ['./add-table.component.css'],
})
export class AddTableComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private tableService = inject(TableService);
  private restaurantService = inject(RestaurantService);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);

  /** Populated from the route on the edit path; null when adding. */
  table: any = null;
  allTables: any[] = [];
  loading = true;

  // Component properties
  tableName1: string = '';
  isDuplicate: boolean = false;
  chairsList = [
    { count: '1', isChecked: false, displayCount: '1' },
    { count: '2', isChecked: true, displayCount: '2' },
    { count: '3', isChecked: false, displayCount: '3' },
    { count: '4', isChecked: false, displayCount: '4' },
    { count: '5', isChecked: false, displayCount: '5' },
    { count: '6', isChecked: false, displayCount: '6' },
    { count: '7', isChecked: false, displayCount: '7' },
    { count: '8', isChecked: false, displayCount: '8' },
    { count: '9', isChecked: false, displayCount: '9' },
    { count: '10', isChecked: false, displayCount: '10' },
    { count: '11', isChecked: false, displayCount: '11' },
    { count: '12', isChecked: false, displayCount: '12' },
    { count: '13', isChecked: false, displayCount: '13' },
    { count: '14', isChecked: false, displayCount: '14' },
    { count: '15', isChecked: false, displayCount: '15' },
    { count: '16', isChecked: false, displayCount: '16+' },
  ];
  shapeList = [
    { shape: 'Square', isChecked: true },
    { shape: 'Rectangle', isChecked: false },
    { shape: 'Circle', isChecked: false },
    { shape: 'Oval', isChecked: false },
  ];

  /** Boxicons glyph per shape, matching the list view's shape column. */
  shapeIcon(shape: string): string {
    switch ((shape || '').toLowerCase()) {
      case 'square':
        return 'bx-square';
      case 'rectangle':
        return 'bx-rectangle';
      case 'circle':
        return 'bx-circle';
      case 'oval':
        return 'bx-shape-circle';
      default:
        return 'bx-shape-square';
    }
  }

  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;
  zones: any = [];
  zone: any;
  shapes = ['Square', 'Rectangle', 'Circle', 'Oval'];
  shape = 'Square';
  dotsCount = 2;

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      this.loadData(id);
    });
  }

  /** Zones drive the picker; the table list backs duplicate-name validation. */
  private loadData(tableId: string | null) {
    this.loading = true;
    this.restaurantService.getRestaurantZones().subscribe({
      next: (zoneRes: FNFResponse<any>) => {
        const zoneData = zoneRes?.data || [];
        this.tableService.getAllTables().subscribe({
          next: (tableRes: FNFResponse<any>) => {
            this.allTables = tableRes?.data || [];
            this.table = tableId
              ? this.allTables.find((t: any) => String(t.table_id ?? t.id) === String(tableId)) ||
                null
              : null;
            this.hydrate(zoneData);
            this.loading = false;
          },
          error: () => this.failLoad(),
        });
      },
      error: () => this.failLoad(),
    });
  }

  private failLoad() {
    this.toastr.error('Unable to load the table form. Please try again later.', 'Error');
    this.loading = false;
    this.router.navigate(['/main/table']);
  }

  private hydrate(zoneData: any[]) {
    if (this.table) {
      this.tableName1 = this.sanitizeTableName(this.table.table_name);
      this.dotsCount = this.table.number_of_seats;
      this.shape = this.table.shape
        ? this.table.shape.charAt(0).toUpperCase() + this.table.shape.slice(1)
        : 'Square';
      this.chairsList.forEach((c) => (c.isChecked = c.count === String(this.dotsCount)));
      this.shapeList.forEach((sh) => (sh.isChecked = sh.shape === this.shape));
    }

    this.zones = zoneData.map((e: any) => ({ zone: e.zone_name, isChecked: false, id: e.id }));
    if (this.zones.length) {
      const match = this.table ? this.zones.find((z: any) => z.id === this.table?.zone?.id) : null;
      const selected = match || this.zones[0];
      selected.isChecked = true;
      this.zone = selected;
    }

    this.drawShape();
  }

  sanitizeTableName(value: string): string {
    return value.replace(/[^a-zA-Z0-9-_]/g, '');
  }

  onTableNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = this.sanitizeTableName(input.value);

    if (input.value !== sanitized) {
      input.value = sanitized;
    }

    this.tableName1 = sanitized;
  }

  // duplicate table name check
  checkDuplicateTableNameValidation() {
    this.isDuplicate = false;
    this.allTables.forEach((table: any) => {
      if (
        (table.table_name === this.tableName1 ||
          table.table_name.toLowerCase() === this.tableName1.toLowerCase()) &&
        table.table_name !== this.table?.table_name
      ) {
        this.isDuplicate = true;
      }
    });
    return this.isDuplicate;
  }

  // Select a chair count and update canvas
  selectChair(chair: any) {
    this.chairsList.forEach((c) => {
      c.isChecked = c.count === chair.count;
      if (c.isChecked) {
        this.dotsCount = parseInt(c.count);
      }
    });
    this.drawShape();
  }

  selectZone(zone: any) {
    this.zones.forEach((c: any) => {
      if (c.zone === zone.zone) {
        c.isChecked = true;
        this.zone = c;
      } else {
        c.isChecked = false;
      }
    });
  }

  // Select a shape and update canvas
  selectShape(shape: any) {
    this.shapeList.forEach((s) => {
      s.isChecked = s.shape === shape.shape;
      if (s.isChecked) {
        this.shape = shape.shape;
      }
    });
    this.drawShape();
  }

  // Draw the selected shape on canvas
  /**
   * Colours resolved at draw time: the canvas cannot read CSS tokens, so the
   * preview would otherwise stay brand-red under every theme.
   */
  private previewPalette() {
    const cs = getComputedStyle(document.body);
    const token = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback;
    return {
      brand: token('--c-brand', '#b31d2c'),
      brandTint: token('--c-brand-tint', '#fdf2f3'),
      surface: token('--c-surface', '#ffffff'),
      border: token('--c-border', '#efece8'),
      text: token('--c-text', '#1f1617'),
      muted: token('--c-text-2', '#6e6264'),
    };
  }

  drawShape() {
    const canvas = this.canvas?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Back the canvas at device resolution so the outlines are not fuzzy.
    const cssWidth = 400;
    const cssHeight = 260;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    canvas.style.width = '100%';
    canvas.style.maxWidth = cssWidth + 'px';
    canvas.style.height = 'auto';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const palette = this.previewPalette();
    const cx = cssWidth / 2;
    const cy = cssHeight / 2;
    const seats = Math.max(1, this.dotsCount || 1);

    // Seats sit outside the table, so the table itself has to leave room for
    // a seat plus its gap on every side.
    const margin = this.SEAT_LENGTH + this.SEAT_GAP + 12;
    const maxW = cssWidth - margin * 2;
    const maxH = cssHeight - margin * 2;

    let w: number;
    let h: number;
    let round: boolean;
    switch (this.shape) {
      case 'Rectangle':
        w = 150 + seats * 10;
        h = 100 + seats * 4;
        round = false;
        break;
      case 'Circle':
        w = h = 100 + seats * 8;
        round = true;
        break;
      case 'Oval':
        w = 150 + seats * 9;
        h = 100 + seats * 5;
        round = true;
        break;
      default:
        w = h = 100 + seats * 9;
        round = false;
    }
    // Never let a 16-seat table run off the canvas.
    const scale = Math.min(1, maxW / w, maxH / h);
    w *= scale;
    h *= scale;

    this.paintTable(ctx, palette, cx, cy, w, h, round);
    const points = round
      ? this.seatPointsOnEllipse(cx, cy, w / 2, h / 2, seats)
      : this.seatPointsOnRect(cx, cy, w, h, seats);
    points.forEach((point) => this.paintSeat(ctx, palette, point));
    this.paintSeatLabel(ctx, palette, cx, cy, seats);
  }

  private readonly SEAT_LENGTH = 20;
  /** Backrest depth, the gap behind the pad, and the pad itself. */
  private readonly SEAT_BACK_DEPTH = 4;
  private readonly SEAT_SPLIT = 3;
  private readonly SEAT_PAD_DEPTH = 9;
  private readonly SEAT_DEPTH = 16; // back + split + pad
  private readonly SEAT_GAP = 9;

  private paintTable(
    ctx: CanvasRenderingContext2D,
    palette: ReturnType<AddTableComponent['previewPalette']>,
    cx: number,
    cy: number,
    w: number,
    h: number,
    round: boolean,
  ) {
    ctx.save();
    ctx.beginPath();
    if (round) {
      ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2);
    } else {
      ctx.roundRect(cx - w / 2, cy - h / 2, w, h, 14);
    }
    // Fill first, then stroke, so the outline is not painted over.
    ctx.fillStyle = palette.surface;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = palette.brand;
    ctx.stroke();
    ctx.restore();
  }

  /** Seat centres and facing angles, spaced evenly around an ellipse. */
  private seatPointsOnEllipse(cx: number, cy: number, rx: number, ry: number, seats: number) {
    const offset = this.SEAT_GAP + this.SEAT_DEPTH / 2;
    const points: { x: number; y: number; angle: number }[] = [];
    for (let i = 0; i < seats; i++) {
      // Start at the top and go clockwise so the first seat reads as "front".
      const t = -Math.PI / 2 + (i * 2 * Math.PI) / seats;
      const nx = Math.cos(t);
      const ny = Math.sin(t);
      points.push({
        x: cx + (rx + offset) * nx,
        y: cy + (ry + offset) * ny,
        angle: Math.atan2(ny, nx) + Math.PI / 2,
      });
    }
    return points;
  }

  /**
   * Seats along a rectangle, allocated to the long sides first so a wide
   * table seats people across from each other rather than bunched at a corner.
   */
  private seatPointsOnRect(cx: number, cy: number, w: number, h: number, seats: number) {
    const offset = this.SEAT_GAP + this.SEAT_DEPTH / 2;
    const perSide = [0, 0, 0, 0]; // top, right, bottom, left
    const weights = [w, h, w, h];
    const total = weights.reduce((a, b) => a + b, 0);
    weights.forEach((weight, i) => {
      perSide[i] = Math.floor((weight / total) * seats);
    });
    let placed = perSide.reduce((a, b) => a + b, 0);
    // Hand out the remainder to the longest sides first.
    const order = [0, 2, 1, 3];
    let k = 0;
    while (placed < seats) {
      perSide[order[k % order.length]] += 1;
      placed += 1;
      k += 1;
    }

    const half = { w: w / 2, h: h / 2 };
    const points: { x: number; y: number; angle: number }[] = [];
    const along = (n: number, i: number) => (n === 1 ? 0.5 : (i + 1) / (n + 1));

    // Local +y has to point at the table, so each side gets its own facing:
    // top 0, right +90, bottom 180, left -90. Sharing an angle between
    // opposite sides leaves half the chairs with their backrest inboard.
    for (let i = 0; i < perSide[0]; i++) {
      points.push({ x: cx - half.w + w * along(perSide[0], i), y: cy - half.h - offset, angle: 0 });
    }
    for (let i = 0; i < perSide[1]; i++) {
      points.push({
        x: cx + half.w + offset,
        y: cy - half.h + h * along(perSide[1], i),
        angle: Math.PI / 2,
      });
    }
    for (let i = 0; i < perSide[2]; i++) {
      points.push({
        x: cx - half.w + w * along(perSide[2], i),
        y: cy + half.h + offset,
        angle: Math.PI,
      });
    }
    for (let i = 0; i < perSide[3]; i++) {
      points.push({
        x: cx - half.w - offset,
        y: cy - half.h + h * along(perSide[3], i),
        angle: -Math.PI / 2,
      });
    }
    return points;
  }

  /**
   * A chair from above: two separate rounded boxes stacked with a gap — the
   * backrest on the outside, the seat pad nearer the table — rather than one
   * box with a line drawn through it.
   *
   * Local +y points at the table, so the backrest sits at negative y.
   */
  private paintSeat(
    ctx: CanvasRenderingContext2D,
    palette: ReturnType<AddTableComponent['previewPalette']>,
    point: { x: number; y: number; angle: number },
  ) {
    ctx.save();
    ctx.translate(point.x, point.y);
    ctx.rotate(point.angle);

    const len = this.SEAT_LENGTH;
    const top = -this.SEAT_DEPTH / 2;

    ctx.fillStyle = palette.brandTint;
    ctx.strokeStyle = palette.brand;
    ctx.lineWidth = 1.5;

    // Backrest — slightly narrower than the pad, as in the design.
    const backLen = len - 3;
    ctx.beginPath();
    ctx.roundRect(-backLen / 2, top, backLen, this.SEAT_BACK_DEPTH, 2);
    ctx.fill();
    ctx.stroke();

    // Seat pad.
    ctx.beginPath();
    ctx.roundRect(
      -len / 2,
      top + this.SEAT_BACK_DEPTH + this.SEAT_SPLIT,
      len,
      this.SEAT_PAD_DEPTH,
      3,
    );
    ctx.fill();
    ctx.stroke();

    ctx.restore();
  }

  private paintSeatLabel(
    ctx: CanvasRenderingContext2D,
    palette: ReturnType<AddTableComponent['previewPalette']>,
    cx: number,
    cy: number,
    seats: number,
  ) {
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = palette.brand;
    ctx.font = '500 12px Inter, ui-sans-serif, system-ui, sans-serif';
    ctx.fillText(`${seats} ${seats === 1 ? 'seat' : 'seats'}`, cx, cy);
    ctx.restore();
  }

  // Add or update a table
  cancel() {
    this.router.navigate(['/main/table']);
  }

  save(tableForm: any) {
    const payload = this.table ? { ...this.table } : ({} as any);
    payload.table_name = tableForm.value;
    payload.number_of_seats = this.dotsCount;
    payload.shape = this.shape.toLowerCase();
    payload.zone = this.zone?.id;

    this.spinner.show();
    const request = this.table
      ? this.tableService.editTable(payload)
      : this.tableService.addNewTable(payload);

    request.subscribe({
      next: (response: FNFResponse<any>) => {
        this.spinner.hide();
        if (!response.data) {
          const message = response.error?.error?.message || response.error?.message;
          this.toastr.error(message || 'No data found!', 'Error');
          return;
        }
        this.toastr.success(
          this.table ? 'Table updated successfully' : 'Table added successfully',
          'Success',
        );
        this.router.navigate(['/main/table']);
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }
}
